/**
 * Carries the Director project a Stripe checkout was started from through the
 * redirect, so /checkout/success can send the buyer straight back into it.
 *
 * Without this the success page's only CTA opened a brand new project. A buyer
 * who hit a credit gate mid-project (Jul 25, project OrfHO9GohAh5pk1EsYa0P)
 * bought a pack, landed on the success page, took the only CTA, and re-ran
 * analyze + brief + references + shot list on the same song in a second
 * project — paying ~56 credits twice and ending up unable to tell which of the
 * two projects held his video.
 *
 * Two carriers, checked in that order by CheckoutSuccess:
 * - Stripe session metadata (`returnProjectId`), the durable copy: survives a
 *   cleared browser or a redirect that lands somewhere else.
 * - the localStorage stash below, keyed to the checkout session id so a stale
 *   entry can never attach an old project to an unrelated purchase.
 */
import { supportedLanguages } from "@/locales";

const STASH_KEY = "solmi_checkout_return";
const CURRENT_PROJECT_KEY = "solmi_director_current_project";
/** Outlives a slow checkout (bank 3DS, wallet app) but not a stale browser. */
const STASH_MAX_AGE_MS = 24 * 60 * 60 * 1000;
/** How long a visited project stays a plausible checkout origin. */
const CURRENT_PROJECT_MAX_AGE_MS = 2 * 60 * 60 * 1000;

const LANGUAGE_PREFIXES = new Set<string>(supportedLanguages);

/**
 * Director project id from a path, tolerating the /:lang prefix and the legacy
 * /ai-music-video-generator/:projectId form.
 */
export function projectIdFromPath(pathname: string): string | null {
  const segments = pathname.split(/[?#]/)[0].split("/").filter(Boolean);
  const start = segments[0] && LANGUAGE_PREFIXES.has(segments[0]) ? 1 : 0;
  const slug = segments[start];
  if (slug !== "ai-music-video" && slug !== "ai-music-video-generator") {
    return null;
  }
  return segments[start + 1] ?? null;
}

/**
 * Remember the project the user is working in. Per-tab (sessionStorage) so the
 * origin survives a full-page detour to /subscription — where the URL no
 * longer names the project — without leaking across tabs or later sessions.
 */
export function rememberDirectorProject(
  projectId: string | null | undefined,
): void {
  if (!projectId) return;
  try {
    sessionStorage.setItem(
      CURRENT_PROJECT_KEY,
      JSON.stringify({ projectId, at: Date.now() }),
    );
  } catch {
    // Storage unavailable (private mode / quota) — checkout just won't carry
    // the project through a /subscription detour.
  }
}

/** Forget the remembered project (leaving the Director, or it went away). */
export function forgetDirectorProject(): void {
  try {
    sessionStorage.removeItem(CURRENT_PROJECT_KEY);
  } catch {
    // ignore
  }
}

/**
 * Which project this checkout is being started from: the URL when the buyer is
 * still on a project page, otherwise the last project they were working in.
 */
export function originatingProjectId(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const fromUrl = projectIdFromPath(window.location.pathname);
  if (fromUrl) return fromUrl;
  try {
    const raw = sessionStorage.getItem(CURRENT_PROJECT_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as { projectId?: string; at?: number };
    if (!parsed?.projectId || typeof parsed.at !== "number") return undefined;
    if (Date.now() - parsed.at > CURRENT_PROJECT_MAX_AGE_MS) return undefined;
    return parsed.projectId;
  } catch {
    return undefined;
  }
}

/**
 * Stash the origin against the Stripe session that was just created. Call from
 * a checkout mutation's onSuccess, before the redirect:
 *
 *   onSuccess: (data, variables) => {
 *     saveCheckoutReturn(data, variables);
 *     if (data.url) window.location.href = data.url;
 *   }
 */
export function saveCheckoutReturn(
  data: { sessionId?: string | null } | null | undefined,
  // `void` in the union: procedures with an optional input (the Plus trial)
  // type their mutation variables that way.
  variables?: { returnProjectId?: string | null } | null | void,
): void {
  const sessionId = data?.sessionId;
  const projectId = variables ? variables.returnProjectId : undefined;
  if (!sessionId || !projectId) return;
  try {
    localStorage.setItem(
      STASH_KEY,
      JSON.stringify({ sessionId, projectId, at: Date.now() }),
    );
  } catch {
    // ignore — Stripe metadata still carries the project id
  }
}

/** The stashed project id, only if it belongs to this checkout session. */
export function readCheckoutReturn(
  sessionId: string | null | undefined,
): string | null {
  if (!sessionId) return null;
  try {
    const raw = localStorage.getItem(STASH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      sessionId?: string;
      projectId?: string;
      at?: number;
    };
    if (!parsed?.projectId || parsed.sessionId !== sessionId) return null;
    if (typeof parsed.at !== "number") return null;
    if (Date.now() - parsed.at > STASH_MAX_AGE_MS) {
      clearCheckoutReturn();
      return null;
    }
    return parsed.projectId;
  } catch {
    return null;
  }
}

export function clearCheckoutReturn(): void {
  try {
    localStorage.removeItem(STASH_KEY);
  } catch {
    // ignore
  }
}
